import * as React from "react";
import { Box, Chip, Paper, Stack, Typography } from "@mui/material";
import { useTranslate } from "react-admin";
import {
  BannerFormValue,
  BannerPreview,
  LOCALE,
  SectionTitleLike,
  getImageSrc,
  t,
} from "../creatorsShared/previewKit";
import {
  CompanyInfo,
  BenefitsDescription,
  PopularServiceRef,
  StoredImage,
  Translatable,
} from "../../types";

interface AboutFormValue {
  sectionImage?: StoredImage;
  sectionTitle?: Translatable<string>;
  sectionSubtitle?: Translatable<string>;
  sectionDescription?: Translatable<string>;
  companyInfo?: Translatable<CompanyInfo[]>;
}

interface BenefitsFormValue {
  sectionTitle?: Translatable<string>;
  sectionSubtitle?: Translatable<string>;
  sectionDescription?: Translatable<string>;
  benefits?: Translatable<BenefitsDescription[]>;
}

interface PopularFormValue {
  sectionTitle?: Translatable<string>;
  sectionSubtitle?: Translatable<string>;
  sectionDescription?: Translatable<string>;
  services?: Translatable<PopularServiceRef[]>;
}

interface MainPageFormValues {
  banner?: BannerFormValue;
  about?: AboutFormValue;
  benefits?: BenefitsFormValue;
  popular?: PopularFormValue;
}

const AboutPreview = ({ about }: { about: AboutFormValue }) => {
  const imgSrc = getImageSrc(about?.sectionImage);
  const companyInfo = about?.companyInfo?.[LOCALE] || [];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 4,
        py: 4,
      }}
    >
      {imgSrc ? (
        <Box
          component="img"
          src={imgSrc}
          alt=""
          sx={{
            flex: 1,
            width: "100%",
            minWidth: 0,
            height: { xs: 330, md: 365 },
            objectFit: "cover",
            borderRadius: 1,
          }}
        />
      ) : (
        <Box sx={{ flex: 1, height: { xs: 330, md: 365 }, bgcolor: "#eee", borderRadius: 1 }} />
      )}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>
        <SectionTitleLike
          title={t(about?.sectionTitle)}
          article={t(about?.sectionSubtitle)}
        />
        {about?.sectionDescription && (
          <Box dangerouslySetInnerHTML={{ __html: t(about.sectionDescription) }} />
        )}
        {companyInfo.map((item, index) => (
          <Stack
            key={index}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ borderBottom: "1px solid rgba(0,0,0,0.1)", py: 1.5 }}
          >
            <Typography sx={{ fontSize: 32, fontWeight: 300 }}>
              {item?.title}
            </Typography>
            <Typography>{item?.description}</Typography>
          </Stack>
        ))}
      </Box>
    </Box>
  );
};

const BenefitsPreview = ({ benefits }: { benefits: BenefitsFormValue }) => {
  const items = benefits?.benefits?.[LOCALE] || [];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 6,
        py: 4,
      }}
    >
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>
        <SectionTitleLike
          title={t(benefits?.sectionTitle)}
          article={t(benefits?.sectionSubtitle)}
        />
        <Typography color="text.secondary">
          {t(benefits?.sectionDescription)}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
        {items.map((item, index) => {
          const iconSrc = getImageSrc(item?.icon);
          return (
            <Stack
              key={index}
              direction="row"
              spacing={3}
              alignItems="center"
              sx={{ borderBottom: "1px solid rgba(0,0,0,0.1)", py: 1.5 }}
            >
              {iconSrc ? (
                <Box component="img" src={iconSrc} alt="" sx={{ width: 40, height: 40 }} />
              ) : (
                <Box sx={{ width: 40, height: 40, bgcolor: "#eee", borderRadius: "50%" }} />
              )}
              <Typography sx={{ flex: 1, fontWeight: 500 }}>{item?.title}</Typography>
              <Typography sx={{ flex: 1 }} color="text.secondary">
                {item?.description}
              </Typography>
            </Stack>
          );
        })}
      </Box>
    </Box>
  );
};

const PopularPreview = ({ popular }: { popular: PopularFormValue }) => {
  const services = popular?.services?.[LOCALE] || [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, py: 4 }}>
      <SectionTitleLike
        title={t(popular?.sectionTitle)}
        article={t(popular?.sectionSubtitle)}
      />
      {popular?.sectionDescription && (
        <Box dangerouslySetInnerHTML={{ __html: t(popular.sectionDescription) }} />
      )}
      <Stack direction="row" flexWrap="wrap" gap={1.5}>
        {services.map((item, index) => (
          <Chip key={index} label={item?.title || "..."} variant="outlined" />
        ))}
      </Stack>
    </Box>
  );
};

const MainPageCreatorRender = ({ formData }: { formData: MainPageFormValues }) => {
  const translate = useTranslate();
  const banner = formData?.banner || {};
  const about = formData?.about || {};
  const benefits = formData?.benefits || {};
  const popular = formData?.popular || {};

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
        {translate("preview.metaHint")}
      </Typography>

      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <BannerPreview banner={banner} />

        <Box sx={{ px: { xs: 3, md: 6 } }}>
          <AboutPreview about={about} />
          <BenefitsPreview benefits={benefits} />
          <PopularPreview popular={popular} />
        </Box>
      </Paper>
    </Box>
  );
};

export default MainPageCreatorRender;
